import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  DoneAll as DoneAllIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import NotificationItem from './NotificationItem';
import { NOTIFICATION_TYPES } from 'src/utils/constants';

/**
 * NotificationDropdown Component
 * Displays list of notifications in a dropdown/popover
 */
function NotificationDropdown({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationClick,
  onRefresh,
  onClose,
  onDeleteAll,
  onDeleteNotification,
  onOpenSettings,
  loading = false
}) {
  const hasNotifications = notifications && notifications.length > 0;
  const hasUnread = notifications?.some(n => !n.is_read);
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  
  // Calculate notification counts for each type
  const notificationCounts = useMemo(() => {
    if (!notifications) return {};
    
    const counts = {
      ALL: notifications.length,
      UNREAD: notifications.filter(n => !n.is_read).length
    };
    
    NOTIFICATION_TYPES.forEach(type => {
      counts[type.type] = notifications.filter(n => n.type === type.type).length;
    });
    
    return counts;
  }, [notifications]);
  
  // Filter notifications based on selected type
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    if (selectedFilter === 'ALL') return notifications;
    if (selectedFilter === 'UNREAD') return notifications.filter(n => !n.is_read);
    return notifications.filter(notification => notification.type === selectedFilter);
  }, [notifications, selectedFilter]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasUnread && (
            <Tooltip title="Mark all as read">
              <IconButton 
                size="small" 
                onClick={onMarkAllAsRead}
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {hasNotifications && (
            <Tooltip title="Clear all notifications">
              <IconButton 
                size="small" 
                onClick={onDeleteAll}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Settings">
            <IconButton size="small" onClick={onOpenSettings}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Filter Dropdown */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <FormControl fullWidth size="small">
          <Select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            startAdornment={<FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />}
            sx={{
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center'
              }
            }}
          >
            <MenuItem value="ALL">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography>All Notifications</Typography>
                <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                  {notificationCounts.ALL || 0}
                </Typography>
              </Box>
            </MenuItem>
            
            <MenuItem value="UNREAD">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography>Unread</Typography>
                <Typography variant="caption" sx={{ ml: 2, color: 'primary.main', fontWeight: 600 }}>
                  {notificationCounts.UNREAD || 0}
                </Typography>
              </Box>
            </MenuItem>
            
            <Divider sx={{ my: 1 }} />
            
            {NOTIFICATION_TYPES.map((type) => (
              <MenuItem key={type.type} value={type.type}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography>{type.label}</Typography>
                  <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                    {notificationCounts[type.type] || 0}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Notification List */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading && !hasNotifications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : hasNotifications && filteredNotifications.length > 0 ? (
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onClick={() => onNotificationClick(notification)}
                  onMarkAsRead={() => onMarkAsRead(notification.id)}
                  onDelete={() => onDeleteNotification(notification.id)}
                  showActions={true}
                />
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : selectedFilter === 'UNREAD' && filteredNotifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 2
            }}
          >
            <Typography variant="h3" sx={{ fontSize: '3rem', mb: 2 }}>🎉</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              You're all caught up!
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              No unread notifications
            </Typography>
          </Box>
        ) : selectedFilter !== 'ALL' && selectedFilter !== 'UNREAD' && filteredNotifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 2
            }}
          >
            <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 2 }}>📭</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              No {NOTIFICATION_TYPES.find(t => t.type === selectedFilter)?.label.toLowerCase()} 
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Try selecting a different filter
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 2
            }}
          >
            <Typography variant="h3" sx={{ fontSize: '3rem', mb: 2 }}>🔔</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              No notifications yet
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              We'll notify you when something important happens
            </Typography>
            <Button 
              variant="outlined" 
              size="small"
              onClick={onOpenSettings}
              sx={{ mt: 1 }}
            >
              Configure Notifications
            </Button>
          </Box>
        )}
      </Box>

    </Box>
  );
}

export default NotificationDropdown;
